import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { Recommendation } from "./Suitability";

const COFFEE = { ROBUSTA: "Robusta", ARABIKA: "Arabika", LIBERIKA: "Liberika" };

export function ClimateView({ c, testid, divider = false }) {
  if (!c) return null;
  return (
    <div className={divider ? "mt-2 border-t border-slate-200 pt-2" : ""} data-testid={testid}>
      <span className="text-xs text-slate-500 block">Profil iklim lokasi (rata-rata {c.period})</span>
      <p><b data-testid={`${testid}-rain`}>{c.rainfallMmYear.toLocaleString("id-ID")} mm/tahun</b> curah hujan · <b data-testid={`${testid}-temp`}>{c.tempMeanC.toLocaleString("id-ID")} °C</b> suhu rata-rata</p>
      <p className="text-emerald-900" data-testid={`${testid}-fit`}>Penilaian kecocokan iklim per jenis kopi: lihat Skor Kesesuaian Lahan Kopi.</p>
      <span className="text-[11px] text-slate-500 block">Data cuaca historis resolusi kasar (±9–25 km), bukan pengukuran lapangan.</span>
    </div>
  );
}

function ClimateProfile({ lat, lng, testid, onDone }) {
  const [s, setS] = useState({ c: null, loading: true, err: false });
  const done = useRef(onDone);
  done.current = onDone;
  useEffect(() => {
    let alive = true;
    setS({ c: null, loading: true, err: false });
    done.current?.(null);
    const t = setTimeout(() => {
      api.get("/public/climate", { params: { lat, lng } })
        .then((r) => { if (alive) { setS({ c: r.data.climate, loading: false, err: !r.data.climate }); done.current?.(r.data.climate ? "ok" : "err"); } })
        .catch(() => { if (alive) { setS({ c: null, loading: false, err: true }); done.current?.("err"); } });
    }, 700);
    return () => { alive = false; clearTimeout(t); };
  }, [lat, lng]);
  if (s.loading) return <p className="mt-2 text-xs text-slate-500" data-testid={`${testid}-loading`}>Memuat profil iklim...</p>;
  if (s.err) return <p className="mt-2 text-xs text-slate-500" data-testid={`${testid}-error`}>Profil iklim belum tersedia untuk titik ini.</p>;
  return <ClimateView c={s.c} testid={testid} divider />;
}

const RETRY_MS = [2500, 6000];
const num = (v, unit) => (v == null ? null : `${v.toLocaleString("id-ID")} ${unit}`);

function PreviewRow({ label, value, testid }) {
  return <div className="flex justify-between gap-2"><span className="text-slate-500">{label}</span><b className={value ? "text-slate-900" : "text-amber-700"} data-testid={testid}>{value || "Belum tersedia"}</b></div>;
}

function SpeciesPreview({ r, testid }) {
  return (
    <div className="rounded-md border bg-white p-2" data-testid={testid}>
      <p className="font-bold text-slate-900">{r.species === "ROBUSTA" ? "Robusta" : "Arabika"}: <span data-testid={`${testid}-score`}>{r.score == null ? "-" : `${r.score}/100`}</span> <span className="font-normal text-slate-600">· {r.categoryLabel}</span></p>
      <p className={`text-xs font-semibold ${r.provisional ? "text-amber-700" : "text-emerald-800"}`} data-testid={`${testid}-label`}>{r.scoreLabel}{r.provisional ? "" : " — belum diverifikasi lapangan"}</p>
      {r.attention?.length > 0 && <p className="text-[11px] text-amber-900">{r.attention.slice(0, 2).join(" · ")}</p>}
    </div>
  );
}

function SuitabilityPreview({ lat, lng, trigger, testid }) {
  const [p, setP] = useState(null);
  useEffect(() => {
    let alive = true;
    setP(null);
    api.get("/public/suitability-preview", { params: { lat, lng }, timeout: 40000 })
      .then((r) => { if (alive) setP(r.data); })
      .catch(() => { if (alive) setP(false); });
    return () => { alive = false; };
  }, [lat, lng, trigger]);
  if (p === null) return <p className="mt-2 text-xs text-slate-500" data-testid={`${testid}-loading`}>Menghitung Skor Kesesuaian...</p>;
  if (!p) return <p className="mt-2 text-xs text-slate-500" data-testid={`${testid}-error`}>Skor Kesesuaian belum dapat dihitung untuk titik ini.</p>;
  const { elevation: e, climate: c, suitability: s } = p;
  const partial = s.ROBUSTA.provisional || s.ARABIKA.provisional;
  const none = s.ROBUSTA.score == null && s.ARABIKA.score == null;
  return (
    <div className="mt-2 border-t border-slate-200 pt-2 space-y-2" data-testid={testid}>
      <span className="text-xs font-bold text-slate-700 block">SKOR KESESUAIAN LAHAN KOPI (pratinjau otomatis)</span>
      <div className="text-xs space-y-0.5">
        <PreviewRow label="Elevasi" value={num(e.value, "mdpl")} testid={`${testid}-elevation`} />
        <PreviewRow label="Curah hujan" value={num(c.rainfallMmYear, "mm/tahun")} testid={`${testid}-rain`} />
        <PreviewRow label="Suhu" value={num(c.tempMeanC, "°C")} testid={`${testid}-temp`} />
        <PreviewRow label="Data lapangan (pH, drainase, lereng, dll.)" value={null} testid={`${testid}-field`} />
      </div>
      {none ? (
        <p className="text-xs text-amber-800" data-testid={`${testid}-none`}>Belum ada data yang bisa dinilai untuk titik ini. Skor akan dihitung saat data tersedia.</p>
      ) : (
        <div className="grid sm:grid-cols-2 gap-2"><SpeciesPreview r={s.ROBUSTA} testid={`${testid}-robusta`} /><SpeciesPreview r={s.ARABIKA} testid={`${testid}-arabika`} /></div>
      )}
      {!none && <Recommendation rec={s.recommendation} testid={`${testid}-recommendation`} />}
      <p className={`text-xs font-semibold ${partial ? "text-amber-700" : "text-slate-600"}`} data-testid={`${testid}-status`}>Status: {none ? "Belum ada skor — menunggu data" : partial ? `Data belum lengkap — skor sementara (cakupan data ${s.ROBUSTA.coveragePercent.toLocaleString("id-ID")}%), bukan skor final` : `Skor Awal dari data peta & cuaca (cakupan data ${s.ROBUSTA.coveragePercent.toLocaleString("id-ID")}%) — data lapangan belum ada / belum diverifikasi`}</p>
      <span className="text-[11px] text-slate-500 block">Parameter yang belum tersedia tidak dihitung sebagai 0 dan tidak memengaruhi nilai. {s.disclaimer}</span>
    </div>
  );
}

function ElevationStatus({ lat, s, testid, onRetry }) {
  if (lat == null) return <b className="text-slate-900" data-testid={`${testid}-value`}>Pilih titik lokasi dulu</b>;
  if (s.loading || (!s.err && s.v == null)) return <b className="text-slate-900" data-testid={`${testid}-value`}>{s.attempt ? `Sumber data lambat, mencoba lagi (${s.attempt}/${RETRY_MS.length})...` : "Menghitung..."}</b>;
  if (s.err) return (
    <div data-testid={`${testid}-unavailable`}>
      <b className="text-amber-800" data-testid={`${testid}-value`}>{s.err === "invalid" ? "Koordinat tidak valid" : "MDPL belum tersedia untuk titik ini"}</b>
      {s.err !== "invalid" && <p className="text-xs text-slate-600">Semua sumber data elevasi sedang tidak merespons. Nilai tidak diisi otomatis. <button type="button" className="font-semibold text-emerald-800 underline" onClick={onRetry} data-testid={`${testid}-retry`}>Coba lagi</button></p>}
    </div>
  );
  return <b className="text-slate-900" data-testid={`${testid}-value`}>{s.v.toLocaleString("id-ID")} mdpl</b>;
}

export function ElevationBox({ point, onValue, testid = "app-elevation" }) {
  const [s, setS] = useState({ v: null, sug: null, loading: false, err: false, attempt: 0 });
  const [nonce, setNonce] = useState(0);
  const [clim, setClim] = useState(null);
  const cb = useRef(onValue);
  cb.current = onValue;
  const lat = point?.[0];
  const lng = point?.[1];
  useEffect(() => {
    let alive = true;
    let t;
    cb.current?.(null);
    if (lat == null) { setS({ v: null, sug: null, loading: false, err: false, attempt: 0 }); return undefined; }
    setS({ v: null, sug: null, loading: true, err: false, attempt: 0 });
    const run = (attempt) => {
      api.get("/public/elevation", { params: { lat, lng } })
        .then((r) => {
          if (!alive) return;
          const v = r.data.elevationMasl;
          if (v != null) {
            setS({ v, sug: r.data.suggestion, src: r.data.source, loading: false, err: false, attempt: 0 });
            cb.current?.(v);
          } else retry(attempt);
        })
        .catch((e) => { if (alive) { e.response?.status === 422 ? setS({ v: null, sug: null, loading: false, err: "invalid", attempt: 0 }) : retry(attempt); } });
    };
    const retry = (attempt) => {
      if (attempt >= RETRY_MS.length) { setS({ v: null, sug: null, loading: false, err: true, attempt: 0 }); return; }
      setS((p) => ({ ...p, attempt: attempt + 1 }));
      t = setTimeout(() => run(attempt + 1), RETRY_MS[attempt]);
    };
    t = setTimeout(() => run(0), 500);
    return () => { alive = false; clearTimeout(t); };
  }, [lat, lng, nonce]);
  return (
    <div className="rounded-lg bg-slate-50 p-3 text-sm space-y-1" data-testid={testid}>
      <span className="text-xs text-slate-500 block">Ketinggian lokasi (MDPL)</span>
      <ElevationStatus lat={lat} s={s} testid={testid} onRetry={() => setNonce((n) => n + 1)} />
      {s.sug && !s.loading && (s.sug.atSea ? (
        <p className="rounded-md border border-red-300 bg-red-50 p-2 font-semibold text-red-800" data-testid={`${testid}-sea-warning`}>{s.sug.text}</p>
      ) : (
        <p className="text-emerald-900" data-testid={`${testid}-suggestion`}>
          <b>Saran jenis kopi: {s.sug.coffeeTypes.length ? s.sug.coffeeTypes.map((c) => COFFEE[c]).join(" / ") : "Kurang ideal"}</b> — {s.sug.text}
        </p>))}
      <span className="text-[11px] text-slate-500 block" data-testid={`${testid}-source`}>Otomatis dari titik lokasi (model elevasi digital, perkiraan{s.src ? ` · sumber: ${s.src}` : ""}). {s.sug?.note}</span>
      {lat != null && !s.sug?.atSea && <ClimateProfile lat={lat} lng={lng} testid={`${testid}-climate`} onDone={setClim} />}
      {lat != null && !s.sug?.atSea && clim && !s.loading && (s.v != null || s.err) && (
        <SuitabilityPreview lat={lat} lng={lng} trigger={`${s.v ?? "x"}-${clim}`} testid={`${testid}-suit`} />)}
    </div>
  );
}
